import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { FlexContainer } from 'features/common/components/FlexContainer';
import { TicketRow } from './TicketRow';
import { Buttons } from './Buttons';
import { PromoCodeInput } from './PromoCodeInput';
import { PriceRow } from './PriceRow';
import { useGetLastEvent } from '../hooks/useGetLastEvent';
import { useCalculateCost } from '../hooks/useCalculateCost';
import { useGetPromoCodeData } from '../hooks/useGetPromoCodeData';
import { handleApplyPromoCodes, handleQuantity } from './utils';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RootState } from 'store';
import { MultidayType } from 'features/common/types';
import { useMobileView } from 'hooks/useMobileView';

import { Container, Currency, Title, TotalPrice } from './OrderSummary.styles';
import { Divider } from '../BoxOffice.styles';

export const OrderSummary = () => {
	const [promoCodeId, setPromoCodeId] = useState<number>(0);
	const [ticketsWithPromoCodes, setTicketsWithPromoCodes] = useState<number[]>([]);
	const [promoCodeApplied, setPromoCodeApplied] = useState<boolean>(true);
	const { t } = useTranslation();
	const { event, isMultiDay } = useGetLastEvent();
	const { promoCodeData } = useGetPromoCodeData({ promoCodeId, setPromoCodeApplied });
	const { multiday } = useAppSelector(({ lastMultiDay }: RootState) => lastMultiDay);
	const { isPunchup } = useAppSelector(({ auth }: RootState) => auth);
	const dispatch = useAppDispatch();
	const isMobile = useMobileView();
	const { setValue } = useFormContext();

	// Send the applied promo code to the backend so the returned subtotal/total are discounted
	// in step with the ticket rows redux already mutates (otherwise the totals show full price).
	const { subtotal, myTaxes, myFees, customFees, totalCost, refetchTotals } = useCalculateCost(promoCodeData?.code);

	const isFree = totalCost === 0 && subtotal === 0;

	useEffect(() => {
		if (event && multiday && !promoCodeApplied) {
			dispatch(
				handleApplyPromoCodes({
					event,
					ticketsWithPromoCodes,
					promoCodeData,
					setPromoCodeApplied,
					isMultiDay,
					multiday,
				})
			);
			refetchTotals();
		}
	}, [event, promoCodeApplied, multiday, isMultiDay]);

	useEffect(() => {
		setValue('total', totalCost.toFixed(2));
		setValue('fixedPrice', subtotal.toFixed(2));
	}, [totalCost, subtotal, setValue]);

	useEffect(() => {
		if (ticketsWithPromoCodes.length) setValue('ticketsWithPC', ticketsWithPromoCodes);
	}, [ticketsWithPromoCodes]);

	const handleTicketRow = () =>
		isMultiDay
			? // TODO: uncomment when multiday feature exits, on the meanwhile we will use multiday[0]
			  // multiday?.map(MultidayTicketRow)
			  (multiday as MultidayType[])[0].ticketTypes.map((ticket) => ticket.quantity > 0 && TicketRow(ticket, isPunchup))
			: event?.ticketTypes.map((ticket) => ticket.quantity > 0 && TicketRow(ticket, isPunchup));

	const handleAtLeastOne = () => Number(handleQuantity({ multiday, event, isMultiDay })) > 0;

	// calculate-totals returns custom_fees as one amount per ticket type (already summed across fee
	// definitions), keyed by ticket_type_id — so it can't be mapped back to individual fee rows.
	// Show a single combined row (named after the lone fee when there's exactly one).
	const customFeesTotal = customFees ? Object.values(customFees).reduce((sum, fee) => sum + fee, 0) : 0;
	const singleCustomFee = (event?.customFees ?? []).length === 1 ? event?.customFees?.[0] : undefined;
	const customFeesLabel = singleCustomFee?.name || t('orderSummary-customFees') || 'Custom Fees';

	return (
		<FlexContainer width={isMobile ? '100%' : '50%'} justifyContent="center">
			{event && multiday && (
				<Container isPunchup={isPunchup}>
					<Title isPunchup={isPunchup}>{t('orderSummary')}</Title>
					<FlexContainer flexDirection="column" gap="8px">
						{handleTicketRow()}
					</FlexContainer>
					<Divider />
					<FlexContainer flexDirection="column">
						<PriceRow title={t('orderSummary-subTotal')} price={subtotal.toFixed(2)} />
						{!isFree && (
							<>
								{customFeesTotal > 0 && <PriceRow title={customFeesLabel} price={customFeesTotal.toFixed(2)} />}
								<PriceRow title={t('fees')} price={myFees.toFixed(2)} />
								<PriceRow title={t('taxes')} price={myTaxes.toFixed(2)} />
							</>
						)}
					</FlexContainer>
					<Divider />
					{!isFree && (
						<FlexContainer justifyContent="space-between">
							<TotalPrice isPunchup={isPunchup}>{t('total')}</TotalPrice>
							<FlexContainer alignItems="center" gap="4px">
								<Currency isPunchup={isPunchup}>USD</Currency>
								<TotalPrice isPunchup={isPunchup}>${totalCost.toFixed(2)}</TotalPrice>
							</FlexContainer>
						</FlexContainer>
					)}
					<PromoCodeInput setPromoCodeId={setPromoCodeId} setTicketsWithPromoCodes={setTicketsWithPromoCodes} />
					<Buttons atLeastOne={handleAtLeastOne()} isFree={isFree} />
				</Container>
			)}
		</FlexContainer>
	);
};
